import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { messagesApi } from '../api/messages';
import NLTWatermark from './NLTWatermark';
import { 
  Plus, X, Camera, Video, Music, Send, ChevronLeft, ChevronRight,
  ThumbsDown, Flame, Guitar, Eye, Loader2, Trash2, Volume2, VolumeX, User, Heart
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Quick emoji reactions fans can tap while watching a story
const STORY_EMOJIS = ['❤️', '🔥', '😂', '😮', '👏', '🙌'];

/**
 * Story Glow Ring Component
 * Shows a colored glow ring around profile pictures when user has unviewed stories
 */
export const StoryGlowRing = ({ userId, children, size = 'md', onClick }) => {
  const { token } = useAuth();
  const [glowInfo, setGlowInfo] = useState(null);

  useEffect(() => {
    if (!userId) return;
    
    const checkGlow = async () => {
      try {
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        const res = await fetch(`${API_URL}/api/radio/stories/check-glow/${userId}`, { headers });
        const data = await res.json();
        setGlowInfo(data);
      } catch (err) {
        console.error('Glow check error:', err);
      }
    };
    
    checkGlow();
    const interval = setInterval(checkGlow, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [userId, token]);

  const sizeClasses = {
    sm: 'p-0.5',
    md: 'p-1',
    lg: 'p-1.5'
  };

  if (!glowInfo?.has_stories || !glowInfo?.has_unviewed) {
    return <div onClick={onClick} className="cursor-pointer">{children}</div>;
  }

  return (
    <div 
      onClick={onClick}
      className={`rounded-full cursor-pointer ${sizeClasses[size]} animate-pulse`}
      style={{ 
        background: `linear-gradient(135deg, ${glowInfo.glow_color}, ${glowInfo.glow_color}88)`,
        boxShadow: `0 0 20px ${glowInfo.glow_color}66`
      }}
    >
      {children}
    </div>
  );
};

/**
 * Stories Bar Component
 * Horizontal scrollable bar showing users with stories
 */
export const StoriesBar = ({ onOpenStory, onCreateStory }) => {
  const { token, user } = useAuth();
  const [storyUsers, setStoryUsers] = useState([]);
  const [myStories, setMyStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    
    const fetchStories = async () => {
      try {
        const [feedRes, myRes] = await Promise.all([
          fetch(`${API_URL}/api/radio/stories/feed`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${API_URL}/api/radio/stories/my`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);
        
        const feedData = await feedRes.json();
        const myData = await myRes.json();
        
        setStoryUsers(feedData.users || []);
        setMyStories(myData.stories || []);
      } catch (err) {
        console.error('Fetch stories error:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchStories();
    const interval = setInterval(fetchStories, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 overflow-x-auto bg-black/40 backdrop-blur-sm">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="flex flex-col items-center gap-0.5 animate-pulse">
            <div className="w-10 h-10 rounded-full bg-gray-700" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 overflow-x-auto scrollbar-hide bg-black/40 backdrop-blur-sm">
      {/* Your Story Button - Play if has stories, Upload if not */}
      <div 
        onClick={() => {
          if (myStories.length > 0) {
            // Play own stories
            console.log('Opening own stories:', { 
              user_id: user?.id, 
              storiesCount: myStories.length 
            });
            onOpenStory({
              user_id: user?.id,
              user_name: user?.name || user?.stageName || 'You',
              user_photo: user?.profilePhoto,
              is_artist: true,
              artist_name: user?.stageName,
              stories: myStories
            });
          } else {
            // No stories - open upload
            onCreateStory();
          }
        }}
        className="flex flex-col items-center cursor-pointer flex-shrink-0"
      >
        <div className="relative">
          {myStories.length > 0 ? (
            // Has stories - show with glow ring
            <div 
              className="p-0.5 rounded-full animate-pulse"
              style={{ background: 'linear-gradient(135deg, #8B5CF6, #EC4899)' }}
            >
              <div className="w-10 h-10 rounded-full bg-gray-800 overflow-hidden border-2 border-gray-900">
                {user?.profilePhoto ? (
                  <img src={user.profilePhoto} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-sm font-bold text-white">
                    {user?.name?.charAt(0) || 'Y'}
                  </div>
                )}
              </div>
            </div>
          ) : (
            // No stories - show your avatar as an add target
            <div className="w-10 h-10 rounded-full bg-gray-800/80 flex items-center justify-center border border-dashed border-gray-600">
              {user?.profilePhoto ? (
                <img src={user.profilePhoto} alt="" className="w-full h-full rounded-full object-cover opacity-50" />
              ) : (
                <Plus className="w-4 h-4 text-gray-400" />
              )}
            </div>
          )}
          {/* Persistent create-story badge - always opens the creator */}
          <button
            onClick={(e) => { e.stopPropagation(); onCreateStory(); }}
            className="absolute -bottom-1 -right-1 w-5 h-5 bg-purple-600 rounded-full flex items-center justify-center border-2 border-gray-900 hover:bg-purple-500 transition-colors shadow-lg"
            title="Create story"
            aria-label="Create story"
            data-testid="create-story-btn"
          >
            <Plus className="w-3.5 h-3.5 text-white" strokeWidth={3} />
          </button>
        </div>
        <span className="text-[9px] text-purple-400 mt-0.5">
          {myStories.length > 0 ? `${myStories.length} active` : 'Your Story'}
        </span>
      </div>

      {/* Other Users' Stories */}
      {storyUsers.map((storyUser) => (
        <div 
          key={storyUser.user_id}
          onClick={() => onOpenStory(storyUser)}
          className="flex flex-col items-center cursor-pointer flex-shrink-0"
        >
          <div 
            className={`p-0.5 rounded-full ${storyUser.has_unviewed ? 'animate-pulse' : ''}`}
            style={{ 
              background: storyUser.has_unviewed 
                ? `linear-gradient(135deg, ${storyUser.glow_color}, ${storyUser.glow_color}88)` 
                : 'transparent'
            }}
          >
            <div className="w-10 h-10 rounded-full bg-gray-700 overflow-hidden border-2 border-gray-900">
              {storyUser.user_photo ? (
                <img src={storyUser.user_photo} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-sm font-bold text-gray-400">
                  {storyUser.user_name?.charAt(0) || '?'}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}

      {storyUsers.length === 0 && myStories.length === 0 && (
        <span className="text-xs text-gray-500 px-2">No stories yet</span>
      )}
    </div>
  );
};

/**
 * Story Viewer Component
 * Full screen story viewer with reactions
 */
export const StoryViewer = ({ storyUser, onClose, onStoryDeleted }) => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [myReaction, setMyReaction] = useState(null);
  const [myEmoji, setMyEmoji] = useState(null);
  const [emojiTotal, setEmojiTotal] = useState(0);
  const [floatingEmojis, setFloatingEmojis] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [replySent, setReplySent] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const progressInterval = useRef(null);
  const videoRef = useRef(null);

  const [stories, setStories] = useState(storyUser?.stories || []);
  const currentStory = stories[currentIndex];
  
  // Check if viewing own stories
  const isOwnStory = user?.id && storyUser?.user_id === user.id;
  
  // Toggle mute/unmute for video
  const toggleMute = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsMuted(!isMuted);
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
    }
  };

  // Visit the story author's profile (works even while a story is playing)
  const goToProfile = (e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    if (!storyUser?.user_id) return;
    onClose();
    navigate(`/profile/${storyUser.user_id}`);
  };
  
  // Update video muted state when it changes
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted, currentIndex]);

  // Handle delete story
  const handleDeleteStory = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    console.log('Delete button clicked', { token: !!token, currentStory: !!currentStory, isOwnStory });
    
    if (!token || !currentStory || !isOwnStory) {
      console.log('Delete blocked:', { hasToken: !!token, hasStory: !!currentStory, isOwn: isOwnStory });
      return;
    }
    
    if (!window.confirm('Delete this story? This cannot be undone.')) return;
    
    setIsDeleting(true);
    try {
      console.log('Deleting story:', currentStory.id);
      const res = await fetch(`${API_URL}/api/radio/stories/${currentStory.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('Delete response:', res.status);
      
      if (res.ok) {
        // Remove from local state
        const newStories = stories.filter((_, i) => i !== currentIndex);
        setStories(newStories);
        
        // Notify parent if callback provided
        if (onStoryDeleted) {
          onStoryDeleted(currentStory.id);
        }
        
        // If no more stories, close viewer
        if (newStories.length === 0) {
          onClose();
        } else if (currentIndex >= newStories.length) {
          // If we deleted the last story, go back one
          setCurrentIndex(newStories.length - 1);
        }
        setProgress(0);
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.detail || 'Failed to delete story');
      }
    } catch (err) {
      console.error('Delete story error:', err);
      alert('Error deleting story');
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    if (!currentStory || isPaused) return;

    // Mark as viewed
    const markViewed = async () => {
      if (token) {
        try {
          await fetch(`${API_URL}/api/radio/stories/${currentStory.id}/view`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          });
        } catch (err) {
          console.error('Mark viewed error:', err);
        }
      }
    };
    markViewed();

    // Progress timer
    const duration = currentStory.media_type === 'video' ? 15000 : 5000;
    const startTime = Date.now();
    
    progressInterval.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newProgress = (elapsed / duration) * 100;
      
      if (newProgress >= 100) {
        nextStory();
      } else {
        setProgress(newProgress);
      }
    }, 50);

    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
  }, [currentIndex, isPaused, currentStory, token]);

  useEffect(() => {
    // Check if user already reacted
    if (currentStory && user) {
      const reactions = currentStory.reactions || {};
      if (reactions.flop?.includes(user.id)) setMyReaction('flop');
      else if (reactions.issa_hit?.includes(user.id)) setMyReaction('issa_hit');
      else if (reactions.rocking_with_it?.includes(user.id)) setMyReaction('rocking_with_it');
      else setMyReaction(null);
    }
  }, [currentStory, user]);

  useEffect(() => {
    // Load emoji reaction summary for the current story
    setFloatingEmojis([]);
    setMyEmoji(null);
    setEmojiTotal(0);
    setReplyText('');
    setReplySent(false);
    if (!currentStory || !token) return;
    let cancelled = false;
    fetch(`${API_URL}/api/radio/stories/${currentStory.id}/emoji-reactions`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        setMyEmoji(data.my_reaction || null);
        setEmojiTotal(data.total || 0);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [currentStory, token]);

  const nextStory = () => {
    if (currentIndex < stories.length - 1) {
      setCurrentIndex(i => i + 1);
      setProgress(0);
    } else {
      onClose();
    }
  };

  const prevStory = () => {
    if (currentIndex > 0) {
      setCurrentIndex(i => i - 1);
      setProgress(0);
    }
  };

  const handleReaction = async (reaction) => {
    if (!token) return;
    
    try {
      await fetch(`${API_URL}/api/radio/stories/${currentStory.id}/react?reaction=${reaction}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setMyReaction(reaction);
    } catch (err) {
      console.error('Reaction error:', err);
    }
  };

  const handleEmojiReact = async (emoji) => {
    if (!token || !currentStory) return;
    // Floating burst animation (feels instant, fire-and-forget for the network call)
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const x = Math.round((Math.random() - 0.5) * 80);
    setFloatingEmojis((f) => [...f, { id, emoji, x }]);
    setTimeout(() => setFloatingEmojis((f) => f.filter((e) => e.id !== id)), 1600);
    setMyEmoji(emoji);
    setEmojiTotal((t) => t + 1);
    try { navigator.vibrate && navigator.vibrate(12); } catch (e) {}
    try {
      await fetch(`${API_URL}/api/radio/stories/${currentStory.id}/emoji-react?emoji=${encodeURIComponent(emoji)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Emoji reaction error:', err);
    }
  };

  const handleSendReply = async () => {
    const text = replyText.trim();
    if (!text || sendingReply || !currentStory || isOwnStory) return;
    const ownerId = storyUser?.user_id;
    if (!ownerId) return;
    setSendingReply(true);
    try {
      await messagesApi.send({
        toUserId: ownerId,
        text,
        story: {
          id: currentStory.id,
          thumbnail: currentStory.thumbnail_url || currentStory.playback_url || currentStory.media_url || '',
          caption: currentStory.caption || '',
        },
      });
      setReplyText('');
      setReplySent(true);
      try { navigator.vibrate && navigator.vibrate(12); } catch (e) {}
      setTimeout(() => setReplySent(false), 2200);
    } catch (err) {
      console.error('Story reply error:', err);
    }
    setSendingReply(false);
  };

  if (!currentStory) return null;

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      {/* Progress Bars */}
      <div className="absolute top-2 left-2 right-2 flex gap-1 z-20">
        {stories.map((_, i) => (
          <div key={i} className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden">
            <div 
              className="h-full bg-white transition-all"
              style={{ 
                width: i < currentIndex ? '100%' : i === currentIndex ? `${progress}%` : '0%'
              }}
            />
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="absolute top-6 left-0 right-0 px-4 flex items-center justify-between z-30">
        <button onClick={goToProfile} data-testid="story-view-profile" className="flex items-center gap-3 text-left">
          <div className="w-10 h-10 rounded-full bg-gray-700 overflow-hidden">
            {storyUser.user_photo ? (
              <img src={storyUser.user_photo} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-sm font-bold">
                {storyUser.user_name?.charAt(0)}
              </div>
            )}
          </div>
          <div>
            <p className="text-white font-medium text-sm flex items-center gap-1">
              {storyUser.artist_name || storyUser.user_name}
            </p>
            <p className="text-white/50 text-xs">
              {new Date(currentStory.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
        </button>
        <div className="flex items-center gap-2">
          {/* Visit profile */}
          <button
            onClick={goToProfile}
            className="px-3 py-2 bg-white/10 hover:bg-white/20 rounded-full transition flex items-center gap-1.5"
            data-testid="story-profile-btn"
          >
            <User className="w-4 h-4 text-white" />
            <span className="text-white text-xs font-medium hidden sm:inline">Profile</span>
          </button>
          {/* Volume control for videos */}
          {currentStory.media_type === 'video' && (
            <button 
              onClick={toggleMute}
              className="p-2 bg-black/50 hover:bg-black/70 rounded-full transition"
              data-testid="story-volume-btn"
            >
              {isMuted ? (
                <VolumeX className="w-5 h-5 text-white" />
              ) : (
                <Volume2 className="w-5 h-5 text-white" />
              )}
            </button>
          )}
          {/* Delete button - only for own stories */}
          {isOwnStory && (
            <button 
              onClick={handleDeleteStory}
              disabled={isDeleting}
              className="p-2 bg-red-500/80 hover:bg-red-600 rounded-full transition disabled:opacity-50"
              data-testid="delete-story-btn"
            >
              {isDeleting ? (
                <Loader2 className="w-5 h-5 text-white animate-spin" />
              ) : (
                <Trash2 className="w-5 h-5 text-white" />
              )}
            </button>
          )}
          <button onClick={onClose} className="p-2">
            <X className="w-6 h-6 text-white" />
          </button>
        </div>
      </div>

      {/* Story Content */}
      <div 
        className="flex-1 flex items-center justify-center relative"
        onTouchStart={() => setIsPaused(true)}
        onTouchEnd={() => setIsPaused(false)}
        onMouseDown={() => setIsPaused(true)}
        onMouseUp={() => setIsPaused(false)}
      >
        {currentStory.media_type === 'video' ? (
          <video 
            ref={videoRef}
            src={currentStory.playback_url || currentStory.media_url}
            className="max-w-full max-h-full object-contain"
            autoPlay
            muted={isMuted}
            playsInline
            loop
          />
        ) : (
          <img 
            src={currentStory.playback_url || currentStory.media_url}
            alt=""
            className="max-w-full max-h-full object-contain"
          />
        )}
        <NLTWatermark size="md" />

        {/* Navigation Areas - lower z-index so buttons work */}
        <div className="absolute inset-0 flex z-10" style={{ top: '80px', bottom: '150px' }}>
          <div className="w-1/3 cursor-pointer" onClick={prevStory} />
          <div className="w-1/3" />
          <div className="w-1/3 cursor-pointer" onClick={nextStory} />
        </div>
      </div>

      {/* Caption */}
      {currentStory.caption && (
        <div className="absolute bottom-64 left-0 right-0 px-4 text-center z-20">
          <p className="text-white bg-black/50 inline-block px-4 py-2 rounded-lg">
            {currentStory.caption}
          </p>
        </div>
      )}

      {/* Attached Track */}
      {currentStory.track && (
        <div className="absolute bottom-48 left-4 right-4 bg-black/60 backdrop-blur-sm rounded-xl p-3 flex items-center gap-3 z-20">
          <Music className="w-5 h-5 text-purple-400" />
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate">{currentStory.track.title}</p>
            <p className="text-gray-400 text-xs truncate">{currentStory.track.artist_name}</p>
          </div>
        </div>
      )}

      {/* Floating emoji reactions burst */}
      <div className="absolute inset-x-0 bottom-24 flex justify-center pointer-events-none z-40" data-testid="story-emoji-float-layer">
        <AnimatePresence>
          {floatingEmojis.map((fe) => (
            <motion.span
              key={fe.id}
              initial={{ opacity: 0, y: 0, scale: 0.6 }}
              animate={{ opacity: [0, 1, 1, 0], y: -220, scale: [0.6, 1.3, 1, 0.9], x: fe.x }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.5, ease: 'easeOut' }}
              className="absolute text-4xl"
              style={{ bottom: 0 }}
            >
              {fe.emoji}
            </motion.span>
          ))}
        </AnimatePresence>
      </div>

      {/* Quick reply (DM) to the artist — fans only */}
      {!isOwnStory && (
        <div className="absolute bottom-32 left-0 right-0 px-4 z-30" data-testid="story-reply-bar">
          <div className="flex items-center gap-2 bg-black/50 backdrop-blur-md rounded-full pl-4 pr-1.5 py-1.5 border border-white/10">
            <input
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onFocus={() => setIsPaused(true)}
              onBlur={() => setIsPaused(false)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSendReply(); }}
              placeholder={replySent ? 'Reply sent ✓' : `Reply to ${storyUser.artist_name || storyUser.user_name || 'story'}…`}
              className="flex-1 bg-transparent text-white text-sm placeholder-white/50 focus:outline-none"
              data-testid="story-reply-input"
              maxLength={500}
            />
            <button
              onClick={handleSendReply}
              disabled={sendingReply || !replyText.trim()}
              className="w-9 h-9 rounded-full bg-gradient-to-r from-[#FF007F] to-[#00F0FF] flex items-center justify-center disabled:opacity-40 transition-opacity shrink-0"
              data-testid="story-reply-send"
              aria-label="Send reply"
            >
              {sendingReply ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : <Send className="w-4 h-4 text-white" />}
            </button>
          </div>
        </div>
      )}

      {/* Quick emoji "send love" bar (fans only, not on your own story) */}
      {!isOwnStory && (
        <div className="absolute bottom-20 left-0 right-0 px-4 flex justify-center z-30" data-testid="story-emoji-bar">
          <div className="flex items-center gap-1 bg-black/50 backdrop-blur-md rounded-full px-2 py-1.5 border border-white/10">
            {STORY_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                onClick={() => handleEmojiReact(emoji)}
                className={`w-10 h-10 rounded-full flex items-center justify-center text-2xl transition-transform hover:scale-125 active:scale-95 ${myEmoji === emoji ? 'bg-white/15' : ''}`}
                data-testid={`story-emoji-${emoji}`}
                aria-label={`React ${emoji}`}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Reaction Buttons */}
      <div className="absolute bottom-4 left-0 right-0 px-4 flex items-center justify-center gap-4 z-30">
        <button 
          onClick={() => handleReaction('flop')}
          className={`p-3 rounded-full transition ${
            myReaction === 'flop' ? 'bg-red-500' : 'bg-white/10 hover:bg-white/20'
          }`}
        >
          <ThumbsDown className="w-5 h-5" />
        </button>
        <button 
          onClick={() => handleReaction('issa_hit')}
          className={`p-3 rounded-full transition ${
            myReaction === 'issa_hit' ? 'bg-orange-500' : 'bg-white/10 hover:bg-white/20'
          }`}
        >
          <Flame className="w-5 h-5" />
        </button>
        <button 
          onClick={() => handleReaction('rocking_with_it')}
          className={`p-3 rounded-full transition ${
            myReaction === 'rocking_with_it' ? 'bg-blue-500' : 'bg-white/10 hover:bg-white/20'
          }`}
        >
          <Guitar className="w-5 h-5" />
        </button>
      </div>

      {/* View Count + love count */}
      <div className="absolute bottom-4 right-4 flex items-center gap-3 text-white/50 text-xs z-30">
        {emojiTotal > 0 && (
          <span className="flex items-center gap-1" data-testid="story-love-count">
            <Heart className="w-4 h-4 text-[#FF007F]" fill="#FF007F" />
            {emojiTotal}
          </span>
        )}
        <span className="flex items-center gap-1">
          <Eye className="w-4 h-4" />
          {currentStory.views?.length || 0}
        </span>
      </div>
    </div>
  );
};

/**
 * Story Creator Component
 * Upload new story with optional track attachment
 */
export const StoryCreator = ({ onClose, onSuccess }) => {
  const { token } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState('');
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [showTrackPicker, setShowTrackPicker] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Fetch tracks for attachment
    const fetchTracks = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/artists/radio/tracks?limit=50`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        const data = await res.json();
        setTracks(data.tracks || []);
      } catch (err) {
        console.error('Fetch tracks error:', err);
      }
    };
    fetchTracks();
  }, []);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    setFile(selectedFile);
    
    // Create preview using object URL for better video handling
    if (selectedFile.type.startsWith('video')) {
      setPreview(URL.createObjectURL(selectedFile));
    } else {
      // Use data URL for images
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file || !token) return;
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (caption) formData.append('caption', caption);
      if (selectedTrack) formData.append('track_id', selectedTrack.id);
      
      const res = await fetch(`${API_URL}/api/radio/stories/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await res.json();
      
      if (data.success) {
        onSuccess && onSuccess(data.story);
        onClose();
      } else {
        alert(data.detail || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-800">
        <button onClick={onClose} className="p-2">
          <X className="w-6 h-6" />
        </button>
        <h2 className="font-semibold">Create Story</h2>
        <button 
          onClick={handleUpload}
          disabled={!file || uploading}
          className="px-4 py-2 bg-purple-600 rounded-full text-sm font-medium disabled:opacity-50"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Share'}
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        {preview ? (
          <div className="relative max-w-full max-h-full">
            {file?.type.startsWith('video') ? (
              <video 
                src={preview} 
                className="max-w-full max-h-[60vh] rounded-xl" 
                controls 
                autoPlay 
                muted 
                playsInline
              />
            ) : (
              <img src={preview} alt="Preview" className="max-w-full max-h-[60vh] rounded-xl" />
            )}
            <button 
              onClick={() => { setFile(null); setPreview(null); }}
              className="absolute top-2 right-2 p-2 bg-black/50 rounded-full"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            <div className="flex gap-4 justify-center">
              <button 
                onClick={() => fileInputRef.current?.click()}
                className="flex flex-col items-center gap-2 p-6 bg-gray-800 rounded-xl hover:bg-gray-700 transition"
              >
                <Camera className="w-10 h-10 text-purple-400" />
                <span className="text-sm">Photo</span>
              </button>
              <button 
                onClick={() => fileInputRef.current?.click()}
                className="flex flex-col items-center gap-2 p-6 bg-gray-800 rounded-xl hover:bg-gray-700 transition"
              >
                <Video className="w-10 h-10 text-blue-400" />
                <span className="text-sm">Video</span>
              </button>
            </div>
            <p className="text-gray-500 text-sm mt-4">
              Photos and videos up to 30 seconds
            </p>
          </div>
        )}
      </div>

      {/* Bottom Options */}
      {preview && (
        <div className="p-4 border-t border-gray-800 space-y-4">
          {/* Caption */}
          <input
            type="text"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Add a caption..."
            className="w-full bg-gray-800 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            maxLength={200}
          />

          {/* Track Attachment */}
          <div>
            <button 
              onClick={() => setShowTrackPicker(!showTrackPicker)}
              className="flex items-center gap-2 text-purple-400 text-sm"
            >
              <Music className="w-4 h-4" />
              {selectedTrack ? selectedTrack.track_title : 'Attach a song'}
            </button>
            
            {showTrackPicker && (
              <div className="mt-2 max-h-40 overflow-y-auto bg-gray-800 rounded-xl">
                {tracks.map(track => (
                  <button
                    key={track.id}
                    onClick={() => { setSelectedTrack(track); setShowTrackPicker(false); }}
                    className={`w-full flex items-center gap-3 p-3 text-left hover:bg-gray-700 ${
                      selectedTrack?.id === track.id ? 'bg-purple-900/50' : ''
                    }`}
                  >
                    <Music className="w-4 h-4 text-gray-400" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{track.track_title}</p>
                      <p className="text-xs text-gray-400 truncate">{track.artist_name}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default { StoryGlowRing, StoriesBar, StoryViewer, StoryCreator };
